import { platformApiRequest } from "./httpClient";

/**
 * Passkey / WebAuthn for the platform-operator realm (`POST /platform/auth/webauthn/*`).
 * Deliberately NOT part of `PlatformApiClient` — same reasoning `tenantSessionClient.ts`
 * already documents for impersonation proof calls: a real WebAuthn ceremony (the browser's
 * actual `navigator.credentials` prompt, a real hardware/biometric signature) has no
 * meaningful mock-mode equivalent, so this only ever gets called when `PLATFORM_API_MODE
 * !== "mock"` (`LoginPage.tsx`/`MyAccountPage.tsx` gate on that directly, mirroring how
 * `ImpersonationViewPage.tsx` gates its own real-only calls) rather than requiring
 * `mockPlatformApiClient.ts` to fake out browser cryptography it cannot honestly simulate.
 *
 * Per §9.1, a passkey only ever replaces the PASSWORD step here — `loginComplete` returns
 * the exact same `{challenge_id, expires_at}` shape `requestLogin` (password) does, never a
 * real session directly; `PlatformAuthContext`'s existing `verifyMfa` finishes the login
 * exactly as it already does for a password-started one.
 */

export interface WebAuthnCredentialOut {
  id: string;
  nickname: string;
  created_at: string;
  last_used_at: string | null;
}

export interface WebAuthnOptionsResponse {
  session_id: string;
  options: Record<string, unknown>;
}

export async function webauthnRegisterBegin(accessToken: string): Promise<WebAuthnOptionsResponse> {
  return platformApiRequest<WebAuthnOptionsResponse>("/auth/webauthn/register/begin", {
    method: "POST",
    accessToken,
  });
}

export async function webauthnRegisterComplete(
  accessToken: string,
  sessionId: string,
  credential: Record<string, unknown>,
  nickname?: string,
): Promise<WebAuthnCredentialOut> {
  return platformApiRequest<WebAuthnCredentialOut>("/auth/webauthn/register/complete", {
    method: "POST",
    accessToken,
    body: { session_id: sessionId, credential, nickname },
  });
}

export async function webauthnListCredentials(accessToken: string): Promise<WebAuthnCredentialOut[]> {
  return platformApiRequest<WebAuthnCredentialOut[]>("/auth/webauthn/credentials", { accessToken });
}

export async function webauthnDeleteCredential(accessToken: string, credentialId: string): Promise<void> {
  await platformApiRequest<void>(`/auth/webauthn/credentials/${credentialId}`, {
    method: "DELETE",
    accessToken,
  });
}

export async function webauthnLoginBegin(): Promise<WebAuthnOptionsResponse> {
  return platformApiRequest<WebAuthnOptionsResponse>("/auth/webauthn/login/begin", { method: "POST" });
}

export async function webauthnLoginComplete(
  sessionId: string,
  credential: Record<string, unknown>,
): Promise<{ challenge_id: string; expires_at: string }> {
  return platformApiRequest<{ challenge_id: string; expires_at: string }>("/auth/webauthn/login/complete", {
    method: "POST",
    body: { session_id: sessionId, credential },
  });
}
